import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isString } from 'lib/string/stringUtils'
import { stripMarkdown } from 'lib/string/stripMarkdown'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useHasMounted from 'hooks/useHasMounted'
import getResizableImage from '../getResizableImage'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'

export const CAROUSEL_CARD_WIDTH = 320

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(300)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
  }

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;

  > picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  &.hover {
    > :nth-child(2) {
      position: absolute;
      inset: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;
  justify-content: space-between;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const IMAGE_ASPECT_RATIO = '16:9'
const IMAGE_WIDTH = 320
const IMAGE_HEIGHT = 180

function getPlainSummaryDescription(description: string) {
  return stripMarkdown(description.replace(/!\[[^\]]*\]\([^)]*\)/g, ''))
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const DetailsContainer = styled(Group)`
  overflow: hidden;

  &.variant-opaque {
    padding-block: 0 ${rem(16)};
    padding-inline: ${rem(16)};
  }
`

const UrgencyLabelsWrapper = styled.div`
  span {
    white-space: normal;
    height: auto;
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  bookmarkButton?: React.ReactElement
  description?: string | React.ReactElement
  descriptionLines?: number
  image: App.Image
  location: string | React.ReactElement
  title: string
  priceDetails?: React.ReactElement
  to: string
  onClick?: () => void
  rating?: App.OfferRating
  urgencyLabels?: React.ReactElement
  partnershipLabel?: React.ReactElement
  loyaltyPointsElement?: React.ReactElement
  loyaltyPackageUpgradeElement?: React.ReactElement
  imageCornerLabel?: React.ReactElement
  images?: Array<App.Image>
  taglineInclusions?: React.ReactElement
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean
}

function CarouselCardSmall({
  bookmarkButton,
  description,
  descriptionLines,
  image: baseImage,
  images,
  location,
  title,
  priceDetails,
  to,
  onClick,
  rating,
  urgencyLabels,
  partnershipLabel,
  className,
  loyaltyPointsElement,
  loyaltyPackageUpgradeElement,
  imageCornerLabel,
  taglineInclusions,
  variant = 'default',
  eagerLoadImage,
  ...rest
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const hoverImage = images?.[1]
  const isDesktop = useScreenSizeUp('tablet')
  const isMounted = useHasMounted()
  const isHoverEnabled = isMounted && isDesktop && !!hoverImage

  const image = useMemo(() => getResizableImage(baseImage ?? images?.[0]), [baseImage, images])
  const resizedHoverImage = useMemo(() => getResizableImage(hoverImage), [hoverImage])
  const plainDescription = useMemo(() => {
    return isString(description) ? getPlainSummaryDescription(description) : undefined
  }, [description])

  return <CarouselCardContainer
    {...rest}
    onClick={onClick}
    target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
    to={to}
    className={clsx(className, `variant-${variant}`)}
  >
    <ImageContainer
      className={clsx({ hover: isHoverEnabled })}
      paletteType="default"
    >
      <Image
        aspectRatio={IMAGE_ASPECT_RATIO}
        image={image}
        fit="center"
        dpr={2}
        height={IMAGE_HEIGHT}
        width={IMAGE_WIDTH}
        onLoad={handleImageLoaded}
        loading={eagerLoadImage ? 'eager' : 'lazy'}
        fetchPriority={eagerLoadImage ? 'high' : undefined}
      />
      {resizedHoverImage && isHoverEnabled && (
        <Image
          aspectRatio={IMAGE_ASPECT_RATIO}
          image={resizedHoverImage}
          fit="center"
          dpr={2}
          height={IMAGE_HEIGHT}
          width={IMAGE_WIDTH}
          loading="lazy"
        />
      )}
      <ImageOverlay>
        <LabelGroup className="left">{partnershipLabel}</LabelGroup>
        {imageCornerLabel}
        <TripGuard>
          <div className="right">
            {bookmarkButton}
          </div>
        </TripGuard>
      </ImageOverlay>
    </ImageContainer>
    <DetailsContainer
      direction="vertical"
      verticalAlign="space-between"
      gap={16}
      className={`variant-${variant}`}
    >
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
          {isString(location) ? <Caption colour="normal-contrast" variant="large" lineClamp={1}>{location}</Caption> : location}
          {rating && <OfferRating
            variant="xs"
            rating={rating}
            onlyNumber
            inlineLabel
            hideLink
          />}
        </Group>
        <Heading lineClamp={3} variant="heading6">{title}</Heading>
        {taglineInclusions}
        {plainDescription && <BodyText colour="light-foreground-alt" lineClamp={descriptionLines || 2} variant="medium" wrap="word-wrap">{plainDescription}</BodyText>}
        {description && !isString(description) && description}
        {loyaltyPackageUpgradeElement}
        {loyaltyPointsElement}
        <UrgencyLabelsWrapper>{urgencyLabels}</UrgencyLabelsWrapper>
      </Group>
      <div>
        {priceDetails}
      </div>
    </DetailsContainer>
  </CarouselCardContainer>
}

export default CarouselCardSmall
